import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { publicAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  Loader2, User, Shield, CheckCircle, AlertCircle, Star,
  Briefcase, Building2, ThumbsUp, ThumbsDown, Send
} from 'lucide-react';

const PublicReference = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    relationship_confirmed: true,
    years_known: 1,
    work_quality_rating: 4,
    teamwork_rating: 4,
    communication_rating: 4,
    reliability_rating: 4,
    strengths: '',
    areas_for_growth: '',
    would_work_again: true,
    additional_comments: ''
  });

  useEffect(() => {
    fetchReferenceRequest();
  }, [token]);

  const fetchReferenceRequest = async () => {
    try {
      const res = await publicAPI.getReferenceRequest(token);
      setData(res.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Reference request not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await publicAPI.submitReference(token, formData);
      setSubmitted(true);
      toast.success('Reference submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit reference');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      await publicAPI.declineReference(token);
      setError('declined');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline request');
    } finally {
      setDeclining(false);
    }
  };

  const RatingSlider = ({ label, value, onChange, description }) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-[#0A0F1E]">{label}</Label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(star => (
            <Star 
              key={star}
              className={`w-4 h-4 ${star <= value ? 'text-amber-400 fill-amber-400' : 'text-[#E8EDF5]'}`}
            />
          ))}
          <span className="text-white font-medium ml-2">{value}/5</span>
        </div>
      </div>
      <Slider
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={1}
        max={5}
        step={1}
        className="py-2"
      />
      {description && <p className="text-[#5B6472] text-xs">{description}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F0F1A] to-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
  }

  if (error === 'declined') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F0F1A] to-[#FFFFFF] flex items-center justify-center p-4">
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#FFFFFF] flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-[#5B6472]" />
          </div>
          <h1 className="text-xl font-bold text-[#0A0F1E] mb-2">Request Declined</h1>
          <p className="text-[#5B6472] mb-6">You have declined to provide this reference.</p>
          <Link to="/">
            <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl">
              Go to VETT Homepage
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F0F1A] to-[#FFFFFF] flex items-center justify-center p-4">
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-[#0A0F1E] mb-2">Request Not Found</h1>
          <p className="text-[#5B6472] mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl">
              Go to VETT Homepage
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F0F1A] to-[#FFFFFF] flex items-center justify-center p-4">
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-[#0A0F1E] mb-2">Thank You!</h1>
          <p className="text-[#5B6472] mb-6">
            Your reference for <span className="text-white font-medium">{data?.candidate?.name}</span> has been submitted successfully.
          </p>
          <Link to="/">
            <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl">
              Learn More About VETT
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F1A] to-[#FFFFFF]" data-testid="public-reference-page">
      {/* Header */}
      <header className="border-b border-[#E8EDF5] bg-[#FFFFFF]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1B5AFF] to-[#1B5AFF]/70 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-[#0A0F1E] text-xl">VETT</span>
          </Link>
          <span className="text-[#5B6472] text-sm">Reference Request</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        {/* Intro Card */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-2xl p-6 sm:p-8 mb-8 animate-fade-in">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {data?.candidate?.photo_url ? (
              <img 
                src={data.candidate.photo_url} 
                alt={data.candidate.name}
                className="w-20 h-20 rounded-2xl object-cover border-2 border-[#E8EDF5]"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-[#FFFFFF] flex items-center justify-center border-2 border-[#E8EDF5]">
                <User className="w-10 h-10 text-[#5B6472]" />
              </div>
            )}
            
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-2xl font-bold text-[#0A0F1E] mb-1">Reference for {data?.candidate?.name}</h1>
              {data?.candidate?.job_title && (
                <p className="text-[#5B6472] flex items-center justify-center sm:justify-start gap-1.5">
                  <Briefcase className="w-4 h-4" />
                  {data.candidate.job_title}
                </p>
              )}
              
              <div className="mt-4 p-3 rounded-xl bg-[#FFFFFF]/50 border border-[#E8EDF5]">
                <p className="text-[#5B6472] text-sm">
                  <span className="text-white font-medium">{data?.requesting_company?.name}</span> has requested your professional reference as their <span className="text-[#1B5AFF]">{data?.referee_relationship}</span>.
                </p>
              </div>
            </div>
          </div>
          
          {data?.custom_message && (
            <div className="mt-6 p-4 rounded-xl bg-[#1B5AFF]/10 border border-[#1B5AFF]/30">
              <p className="text-[#5B6472] text-sm italic">"{data.custom_message}"</p>
            </div>
          )}
        </Card>

        {/* Reference Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-2xl p-6 sm:p-8 animate-fade-in">
            <h2 className="text-lg font-semibold text-white mb-6">Performance Ratings</h2>
            
            <div className="space-y-6">
              <RatingSlider
                label="Work Quality"
                value={formData.work_quality_rating}
                onChange={(v) => setFormData(prev => ({ ...prev, work_quality_rating: v }))}
                description="Quality and accuracy of their work output"
              />
              
              <RatingSlider
                label="Teamwork"
                value={formData.teamwork_rating}
                onChange={(v) => setFormData(prev => ({ ...prev, teamwork_rating: v }))}
                description="Collaboration and ability to work with others"
              />
              
              <RatingSlider
                label="Communication"
                value={formData.communication_rating}
                onChange={(v) => setFormData(prev => ({ ...prev, communication_rating: v }))}
                description="Clarity and effectiveness in communication"
              />
              
              <RatingSlider
                label="Reliability"
                value={formData.reliability_rating}
                onChange={(v) => setFormData(prev => ({ ...prev, reliability_rating: v }))}
                description="Dependability and meeting commitments"
              />
            </div>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-2xl p-6 sm:p-8 animate-fade-in">
            <h2 className="text-lg font-semibold text-white mb-6">Detailed Feedback</h2>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Key Strengths</Label>
                <Textarea
                  value={formData.strengths}
                  onChange={(e) => setFormData(prev => ({ ...prev, strengths: e.target.value }))}
                  placeholder="What are their greatest professional strengths?"
                  className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl min-h-[100px]"
                  data-testid="strengths-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Areas for Growth</Label>
                <Textarea
                  value={formData.areas_for_growth}
                  onChange={(e) => setFormData(prev => ({ ...prev, areas_for_growth: e.target.value }))}
                  placeholder="Any areas where they could improve?"
                  className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl min-h-[100px]"
                  data-testid="growth-areas-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#0A0F1E]">Additional Comments</Label>
                <Textarea
                  value={formData.additional_comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, additional_comments: e.target.value }))}
                  placeholder="Any other information you'd like to share..."
                  className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl min-h-[80px]"
                  data-testid="additional-comments-input"
                />
              </div>
            </div>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-2xl p-6 sm:p-8 animate-fade-in">
            <h2 className="text-lg font-semibold text-white mb-6">Final Questions</h2>
            
            <div className="space-y-6">
              <div>
                <Label className="text-[#0A0F1E] mb-3 block">Would you work with this person again?</Label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, would_work_again: true }))}
                    className={`flex-1 rounded-xl h-12 ${
                      formData.would_work_again
                        ? 'bg-green-500/20 border-green-500/50 text-green-400 hover:bg-green-500/30'
                        : 'bg-[#FFFFFF] border-[#E8EDF5] text-[#5B6472] hover:border-[#1B5AFF]'
                    } border`}
                    data-testid="would-work-again-yes"
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Yes
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, would_work_again: false }))}
                    className={`flex-1 rounded-xl h-12 ${
                      !formData.would_work_again
                        ? 'bg-red-500/20 border-red-500/50 text-red-400 hover:bg-red-500/30'
                        : 'bg-[#FFFFFF] border-[#E8EDF5] text-[#5B6472] hover:border-[#1B5AFF]'
                    } border`}
                    data-testid="would-work-again-no"
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    No
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="text-[#0A0F1E] mb-3 block">Confirm your professional relationship</Label>
                <Button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, relationship_confirmed: !prev.relationship_confirmed }))}
                  className={`w-full rounded-xl h-12 justify-start ${
                    formData.relationship_confirmed
                      ? 'bg-[#1B5AFF]/20 border-[#1B5AFF]/50 text-[#1B5AFF] hover:bg-[#1B5AFF]/30'
                      : 'bg-[#FFFFFF] border-[#E8EDF5] text-[#5B6472] hover:border-[#1B5AFF]'
                  } border`}
                  data-testid="confirm-relationship-btn"
                >
                  <CheckCircle className={`w-4 h-4 mr-2 ${formData.relationship_confirmed ? 'opacity-100' : 'opacity-30'}`} />
                  I confirm I worked with {data?.candidate?.name} as their {data?.referee_relationship}
                </Button>
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              onClick={handleDecline}
              disabled={declining}
              variant="outline"
              className="flex-1 border-[#E8EDF5] text-[#5B6472] hover:bg-[#FFFFFF] hover:text-white rounded-xl h-12"
              data-testid="decline-reference-btn"
            >
              {declining ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              I prefer not to provide a reference
            </Button>
            <Button
              type="submit"
              disabled={submitting || !formData.relationship_confirmed}
              className="flex-1 bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl h-12"
              data-testid="submit-reference-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Reference
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="mt-12 text-center text-[#5B6472] text-sm">
          <p>Your reference will be shared with {data?.requesting_company?.name} via VETT.</p>
          <p className="mt-1">Your response is confidential and used only for employment verification.</p>
        </div>
      </main>
    </div>
  );
};

export default PublicReference;
